import React, { useState } from "react";
import {
  RightSidebar,
  RightSidebarHeader,
  RightSidebarContent,
  RightSidebarSaveFooter
} from "../RightSidebar";
import { FormGroup, InputGroup, Intent, ProgressBar } from "@blueprintjs/core";
import VideoUploadForm from "./VideoUploadForm";
import ProductSelect from "./ProductSelect";
import useMaskedInputState from "../../hooks/useMaskedInputState";
import { SubscriptionLimit } from "../SubscriptionLimit";
import { Link } from "react-router-dom";
import { useProductList, useFileUpload, useCreateVideo } from "./hooks";
import styles from "./VideoUploadSidebar.module.scss";

const Selector = React.memo(function({
  productsLoading,
  products,
  selectedProduct,
  setSelectedProduct
}) {
  return (
    <FormGroup
      className={styles.formGroup}
      label="2. Choose a Product"
      helperText={
        <>
          You must choose one of your products to pair with your video. If you
          do not have your product added, please{" "}
          <Link to="/products">add your product here</Link>.
        </>
      }
    >
      <ProductSelect
        loading={productsLoading}
        products={products}
        value={selectedProduct}
        onChange={(p) => setSelectedProduct(p)}
      />
    </FormGroup>
  );
});

function VideoUploadSidebar({
  onDismiss: onSidebarDismiss,
  refetchVideos,
  sidebarIsOpen
}) {
  const {
    uploading,
    progress,
    uploadedKey,
    upload,
    resetUpload
  } = useFileUpload();
  const { products, loading: productsLoading } = useProductList();
  const [selectedProduct, setSelectedProduct] = useState();
  const [videoName, setVideoName] = useMaskedInputState("", {
    maxLength: 200,
    regex: /[^ ]/
  });
  const onDismiss = () => {
    resetUpload();
    setVideoName("");
    setSelectedProduct();
    onSidebarDismiss();
  };
  const { loading: createVideoLoading, createVideo } = useCreateVideo({
    refetchVideos,
    onDismiss
  });

  return (
    <RightSidebar
      className={styles.videoUploadSidebar}
      sidebarIsOpen={sidebarIsOpen}
    >
      <RightSidebarHeader
        title={"Upload a Video"}
        onDismiss={onDismiss}
        isDismissDisabled={false}
      />
      <RightSidebarContent>
        <SubscriptionLimit type="videos" />
        <FormGroup label="1. Upload Your Video">
          <p className={styles.formGroupExplanation}>
            Choose a video from your computer to upload to Video Squirrel
          </p>

          <VideoUploadForm
            uploading={uploading}
            progress={progress}
            onFileChange={(file) => upload(file)}
          />
        </FormGroup>

        <Selector
          productsLoading={productsLoading}
          products={products}
          selectedProduct={selectedProduct}
          setSelectedProduct={setSelectedProduct}
        />

        <FormGroup
          className={styles.formGroup}
          label="3. Name Your Video"
          helperText="Your video name will be displayed when you share your video to your followers."
        >
          <InputGroup
            autoCapitalize="words"
            value={videoName}
            onChange={(e) => setVideoName(e.target.value)}
          />
        </FormGroup>
      </RightSidebarContent>

      <RightSidebarSaveFooter
        onCancel={onDismiss}
        onSave={() => createVideo(videoName, selectedProduct.id, uploadedKey)}
        pageIsLoading={productsLoading || createVideoLoading}
        disableSave={
          !selectedProduct || !videoName || !(uploading && progress === 1)
        }
      />
    </RightSidebar>
  );
}

export default VideoUploadSidebar;
